import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { PortableText, PortableTextProps } from '@portabletext/react'
import { Form, FormField, FormFieldState, useFormState } from '../or-booking-panel'
import { OrTextBlockProps, textBlockBaseComponents } from '../or-text-block'
import { AtLink, AtLinkTarget, AtLinkType, NavArrowRight } from '../../atoms'
import { MlNotification, MlNotificationProps } from '../../molecules'
import { buildFormFieldInitialState, findBlockIdx, getErrorMessages, prepareErrors, replaceBlock } from '../../utils'

const VALIDATION_ERROR_NOTIFICATION_ID = 'or-form-validation-notification'

export interface FormRendererProps {
  fields: FormField[]
  title?: OrTextBlockProps['content']
  isLoading: boolean
  onSubmit?: (valid: boolean, errors: string[], state: FormFieldState) => Promise<void>
  onGateUnlock?: () => void
  gaFormField: string
  gaCookie: string
  isGateUnlocked?: boolean
  gateAttachedFileUrl?: string
  /** When true, file is sent by email and download link is not shown */
  sendGatedContentByEmail?: boolean
  /** From Sanity orForm singleFieldErrorNotification — shown when one field fails validation */
  fieldError?: Omit<MlNotificationProps, 'className'>
  /** From Sanity orForm multipleFieldsErrorNotification — shown when several fields fail */
  multipleFieldError?: Omit<MlNotificationProps, 'className'>
}

export const FormRenderer = ({
  fields,
  title,
  isLoading,
  onSubmit,
  onGateUnlock,
  gaFormField,
  gaCookie,
  isGateUnlocked,
  gateAttachedFileUrl,
  sendGatedContentByEmail,
  fieldError,
  multipleFieldError,
}: FormRendererProps) => {
  const initialState = useMemo(
    () => buildFormFieldInitialState(fields, gaFormField, gaCookie),
    [fields, gaFormField, gaCookie]
  )

  const [state, handler, reset] = useFormState(initialState)
  const [validationErrorFields, setValidationErrorFields] = useState<string[] | null>(null)

  /** Sync GA hidden field when cookie loads — avoids remounting the form. */
  useEffect(() => {
    if (!gaFormField) return
    handler({ [gaFormField]: { value: gaCookie } })
  }, [gaFormField, gaCookie, handler])

  const validationNotification = useMemo((): MlNotificationProps | null => {
    if (!validationErrorFields?.length) return null
    const multiple = validationErrorFields.length > 1
    const base = multiple ? multipleFieldError ?? fieldError : fieldError ?? multipleFieldError
    if (!base) return null

    const errorTexts = getErrorMessages(fields)
    const generateContent = (block: PortableTextProps['value']): PortableTextProps['value'] => {
      if (!Array.isArray(block)) return block
      const idx = findBlockIdx(block as { children: { text: string }[] }[])
      if (idx < 0) return block
      return replaceBlock(
        block as object[],
        idx,
        prepareErrors(validationErrorFields, errorTexts)
      ) as PortableTextProps['value']
    }

    const fieldWithError = (e: MouseEvent) => {
      e.preventDefault()
      document.getElementById(`${validationErrorFields[0]}-field`)?.focus()
    }

    const pointingProps = {
      onClick: fieldWithError,
      actionTarget: `#${validationErrorFields[0]}-field`,
      IconRightRef: NavArrowRight,
    }

    return {
      ...base,
      body: generateContent(base.body),
      link: {
        ...base.link,
        ...pointingProps,
      },
    }
  }, [validationErrorFields, fieldError, multipleFieldError, fields])

  if (isGateUnlocked && gateAttachedFileUrl && !sendGatedContentByEmail) {
    return (
      <div className="flex flex-col gap-4 mt-6">
        <p className="font-heading font-medium text-neutrals-900">Download your content:</p>
        <AtLink
          className="!justify-start"
          type={AtLinkType.SECONDARY}
          title="Download file"
          actionTarget={gateAttachedFileUrl}
          target={AtLinkTarget.BLANK}
        />
      </div>
    )
  }

  return (
    <>
      {validationNotification ? (
        <MlNotification {...validationNotification} id={VALIDATION_ERROR_NOTIFICATION_ID} className="my-10" />
      ) : null}
      {title ? <PortableText value={title} components={textBlockBaseComponents} /> : null}
      <Form
        state={state}
        fields={fields.map((field) =>
          field.type === 'submit' && isLoading ? { ...field, isDisabled: isLoading } : field
        )}
        formClassName="!pt-0 !pb-0"
        fieldClassName="gap-4"
        gridClassName="slg:grid-cols-2"
        ctaClassName="sm:w-[270px] slg:w-[282px] lg:w-[396px] sm:self-center"
        skipCtaBar
        onChange={(change) => handler(change)}
        onSubmit={async (valid, errors) => {
          if (!valid) {
            setValidationErrorFields(errors)
            return
          }
          setValidationErrorFields(null)
          try {
            await onSubmit?.(valid, errors, state)
            onGateUnlock?.()
            reset()
          } catch {
            // Server/network error — parent shows error; do not unlock gate
          }
        }}
      />
    </>
  )
}
